/**
 * Onboarding · intra-module navigation config (RW-1024).
 *
 * Single source of truth for the module's destination sets — mirrors the
 * design's `SNV_NAV` (rollout/rp-onboarding-subnav.jsx). Role-scoped, two forms:
 *   • new hire  → a calm 3-stop horizontal bar  (OnboardingBar)
 *   • staff/ops → a grouped, collapsible left rail (OnboardingRail)
 *
 * The bar drives the agent onboarding ROUTES (App.js); the rail drives the
 * admin onboarding tab's in-place sub-views (AdminOnboardingTab `subTab`).
 * Keep the two consumers pointed at THIS config so the destinations stay in
 * sync (RW-733: role/visibility reuses the shared onboarding permission logic;
 * see canSeeStaffOnboardingNav below).
 */
import { isAdmin } from '../../lib/permissions';

/* ───────── New-hire bar — 3 destinations, mapped to agent routes ─────────
   RW-1237 nav honesty: the second stop opens the Agent Information FORM
   (/onboarding/agent-info), not a task list — label it "My info"; and the
   checklist stop reads "My checklist" instead of the vague "Overview". ids and
   paths stay stable (activeHireId + App.js's OnboardingBar key off them). */
export const HIRE_NAV = [
  { id: 'overview', icon: 'home',      name: 'My checklist', path: '/onboarding' },
  { id: 'tasks',    icon: 'clipboard', name: 'My info',      path: '/onboarding/agent-info' },
  { id: 'status',   icon: 'pin',       name: 'Status',       path: '/onboarding/status' },
];

/** Which bar tab is active for the current route. */
export function activeHireId(pathname) {
  if (pathname === '/onboarding/sign') return 'sign';
  if (pathname === '/onboarding/status' || pathname === '/onboarding/success') return 'status';
  if (pathname === '/onboarding/agent-info' || pathname === '/onboarding/lrec') return 'tasks';
  return 'overview';
}

/* ───────── Staff rail — grouped destinations (mirror SNV_NAV.staff) ─────────
   `id` is the AdminOnboardingTab `subTab` this destination renders in place.
   `route` (optional) deep-links OUT of the tab to a destination that lives in
   its own module (Offboarding). */
// RW-1724: `tracker` is an INTENTIONAL alias of the design vocabulary — it
// renders as "Pipeline". The id persists in AdminOnboardingTab's subTab state
// and in deep links, so renaming it to match the design would break both; the
// label is what users (and the design) see.
// RW-1703: the Automation group (`nudges` "Reminders" + `emails` "Emails") is
// GONE — the RW-985/986 rules/templates stack it configured is retired (zero
// rules and zero template sends in its lifetime). Daily reminders now run
// unconditionally via onboardingEngineNudges; their knobs (kill switch, quiet
// hours, admin summary) live in Settings.
export const STAFF_NAV_GROUPS = [
  { label: 'Manage', items: [
    { id: 'tracker',     icon: 'chart',     name: 'Pipeline' },
    { id: 'finance',     icon: 'dollar',    name: 'Finance queue' },
    { id: 'invite',      icon: 'at',        name: 'Invite' },
    { id: 'templates',   icon: 'doc',       name: 'Templates' },
    // RW-3911: the onboarding signature forms, edited in DocuSeal's embedded editor — admins only.
    { id: 'forms',       icon: 'sign',      name: 'Forms', adminOnly: true },
  ] },
  { label: 'Provisioning', items: [
    { id: 'credentials', icon: 'key',       name: 'Credentials' },
    { id: 'access',      icon: 'lock',      name: 'Access' },
    { id: 'swag',        icon: 'package',   name: 'Welcome kit' },
  ] },
  { label: 'Lifecycle', items: [
    { id: 'pilot',       icon: 'sparkles',  name: 'Wave 1 cutover' },
    // RW-1633: /admin/offboarding mounts admin-only (App.js) — without this
    // flag the rail showed the destination to non-admin staff, who landed on
    // a blank pane.
    { id: 'offboard',    icon: 'arrR',      name: 'Offboarding', route: '/admin/offboarding', adminOnly: true },
  ] },
  // Onboarding "make it real" Phase 1.5 — module-owned admin config home. The
  // `adminOnly` flag restricts this destination to admins (see
  // visibleStaffNavGroups); the rest of the rail stays at staff-level visibility.
  { label: 'Configuration', items: [
    { id: 'settings',    icon: 'shield',    name: 'Settings', adminOnly: true },
  ] },
];

/** Flat id → meta lookup (includes the group label). */
export const STAFF_NAV_INDEX = STAFF_NAV_GROUPS.reduce((acc, g) => {
  g.items.forEach((it) => { acc[it.id] = { ...it, group: g.label }; });
  return acc;
}, {});

/** Ordered list of in-tab subTab ids the rail can land on (excludes route-only). */
export const STAFF_TAB_IDS = STAFF_NAV_GROUPS
  .flatMap((g) => g.items)
  .filter((it) => !it.route)
  .map((it) => it.id);

/**
 * Defensive role gate (RW-1024 acceptance: "agents never see staff
 * destinations"). The route already gates the admin surface to staff, so this
 * is intent-explicit belt-and-suspenders, tolerant of both the new roles[]
 * shape and the legacy single `role` string. Kept import-light to avoid the
 * theme/roles TDZ pitfalls (CLAUDE.md Gotcha #11) — no module-level derivation.
 */
const STAFF_ROLE_TOKENS = [
  'agent_services', 'tc', 'trainer', 'broker',
  'office_coordinator', 'marketing', 'relo_services',
];
export function canSeeStaffOnboardingNav(user) {
  if (!user) return false;
  if (isAdmin(user)) return true; // admin + super_admin
  const raw = user.roles != null ? user.roles : user.role;
  const roles = Array.isArray(raw) ? raw : (raw ? [raw] : []);
  return roles.some((r) => STAFF_ROLE_TOKENS.includes(r));
}

/**
 * Rail groups visible to THIS operator. Destinations flagged `adminOnly` (e.g.
 * the module Settings home) are dropped for non-admins; everything else stays at
 * the rail's staff-level visibility (canSeeStaffOnboardingNav). Empty groups are
 * pruned so a non-admin never sees an empty "Configuration" header. The render
 * gate is belt-and-suspenders only — the real boundary is the admin-only write
 * rule on settings/{id} (firestore.rules) and the isAdmin guard at the subTab.
 */
export function visibleStaffNavGroups(user) {
  const admin = isAdmin(user);
  return STAFF_NAV_GROUPS
    .map((g) => ({ ...g, items: g.items.filter((it) => !it.adminOnly || admin) }))
    .filter((g) => g.items.length > 0);
}
